import React from 'react';
import { Dices, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { DigitBoxInput } from '../ui/DigitBoxInput';
import {
  BET_TYPES,
  BET_TYPE_LABELS,
  DIGIT_TAB_OPTIONS,
  TICKET_NUMBER_LENGTH,
  TICKET_TABS,
  getTicketCountByBetType,
} from '../../utils/ticketNumberUtils';

function BetTypeSelector({ betType, onChange, sampleNumber, digitLength }) {
  const straightCount = 1;
  const permutationCount =
    sampleNumber.length === digitLength
      ? getTicketCountByBetType(sampleNumber, BET_TYPES.PERMUTATION)
      : 0;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">รูปแบบการซื้อ</p>
      <div className="grid grid-cols-2 gap-3">
        {[BET_TYPES.STRAIGHT, BET_TYPES.PERMUTATION].map((type) => {
          const count =
            type === BET_TYPES.STRAIGHT ? straightCount : permutationCount;
          return (
            <button
              key={type}
              type="button"
              onClick={() => onChange(type)}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                betType === type
                  ? 'border-brand-red bg-red-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-brand-gold'
              }`}
            >
              <p className="font-semibold text-brand-red">
                {BET_TYPE_LABELS[type]}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {type === BET_TYPES.STRAIGHT
                  ? 'แต่ละเลข = 1 ใบ (แยกใบ)'
                  : `แต่ละเลขขยายเป็นโต๊ด (${count || '...'} ใบ/เลข)`}
              </p>
            </button>
          );
        })}
      </div>
      {betType === BET_TYPES.PERMUTATION && (
        <p className="text-xs text-gray-500">
          โต๊ดจะสลับเลขท้ายทุกแบบ แยกเป็นใบ ๆ ส่วนหน้าสุ่มอัตโนมัติให้ครบ{' '}
          {TICKET_NUMBER_LENGTH} หลัก
        </p>
      )}
    </div>
  );
}

export function Random10PackEditor({
  digitTab,
  onDigitTabChange,
  betType,
  onBetTypeChange,
  numbers,
  onChangeNumber,
  onReroll,
  duplicateIndexes,
}) {
  const digitOption = DIGIT_TAB_OPTIONS.find((t) => t.id === digitTab);
  const digitLength = digitOption?.digitLength ?? TICKET_NUMBER_LENGTH;
  const isSixDigit = digitTab === TICKET_TABS.NORMAL;
  const sampleNumber = numbers.find((n) => n.length === digitLength) || '';

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50/40 p-3 text-sm text-gray-700">
        <Dices className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
        <p>
          สุ่มเลข {digitLength} หลัก × 10 ใบ (ไม่ซ้ำ) แก้เลขทีละใบได้
          {isSixDigit
            ? ' — ซื้อเลข 6 หลักตรงทีละใบ'
            : ' — เลือกเต็ง/โต๊ดได้ แต่ละฐานแยกใบ'}
        </p>
      </div>

      <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1">
        {DIGIT_TAB_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onDigitTabChange(opt.id)}
            className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-colors sm:text-sm ${
              digitTab === opt.id
                ? 'bg-white text-brand-red shadow-sm'
                : 'text-gray-600 hover:text-brand-red'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {!isSixDigit && (
        <BetTypeSelector
          betType={betType}
          onChange={onBetTypeChange}
          sampleNumber={sampleNumber}
          digitLength={digitLength}
        />
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">เลขทั้ง 10 ใบ</p>
        <Button type="button" variant="outline" size="sm" onClick={onReroll}>
          <RefreshCw className="h-4 w-4" />
          สุ่มใหม่
        </Button>
      </div>

      <div
        className={`grid gap-2 ${isSixDigit ? 'grid-cols-2' : 'grid-cols-5'}`}
      >
        {numbers.map((value, index) => {
          const isDup = duplicateIndexes.has(index);
          return (
            <div
              key={index}
              className={`rounded-xl border p-3 ${
                isDup
                  ? 'border-brand-red bg-red-50/60'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <p className="mb-2 text-xs font-medium text-gray-500">
                ใบที่ {index + 1}
                {isDup ? (
                  <span className="ml-2 text-brand-red">เลขซ้ำ</span>
                ) : null}
              </p>
              <DigitBoxInput
                id={`random10-${index}`}
                length={digitLength}
                value={value}
                onChange={(next) => onChangeNumber(index, next)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
